import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

let globalOtp = ''; // Global variable to store OTP

// Validation schema using Yup for Business Enquiry form
const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    mobileNo: Yup.string()
        .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
        .required('Mobile number is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    city: Yup.string().required('City is required'),
    pincode: Yup.string().matches(/^\d{6}$/, 'Pincode must be 6 digits').required('Pincode is required'),
    company: Yup.string().required('Company name is required'),
    businessType: Yup.string().required('Business type is required'),
    estimatedPickupDaily: Yup.string().required('Estimated daily pickup is required'),
    contentOfTheShipment: Yup.string().required('Content of the shipment is required'),
    wantedServiceType: Yup.string().required('Service type is required'),
    callMailAccepted: Yup.bool().oneOf([true], 'You must accept the terms'),
});

const errorMessageStyle = {
    color: 'red',
    fontSize: '0.9em',
    marginTop: '4px',
};

const BusinessEnquiryForm = () => {
    const [otpSent, setOtpSent] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [thankYouMessage, setThankYouMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(60); // Timer for OTP expiration
    const [timerActive, setTimerActive] = useState(false); // To control the timer
    const [otpResendEnabled, setOtpResendEnabled] = useState(false);
    const [mobileDisabled, setMobileDisabled] = useState(false); 
    // Handle sending OTP
    const handleSendOtp = async (mobileNo) => {
        if (!mobileNo) return; // Prevent sending OTP if mobile number is empty

        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/CheckValidNumberOrNot?mobileNo=${mobileNo}`);
            if (response.data.success) {
                setOtpSent(true);
                setOtpError('');
                setOtpInput(''); // Clear any previous OTP input
                setOtpVerified(false); // Reset verification status
                setTimer(60); // Reset the timer
                setTimerActive(true); // Start the timer
                setOtpResendEnabled(false); // Disable resend button
                setMobileDisabled(true); 
            } else {
                setOtpError('Failed to send OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setOtpError('An error occurred while sending OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle OTP verification
    const handleOtpVerify = async (mobileNo) => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/VerifyOtp?mobileNo=${mobileNo}&otp=${otpInput}`);
            if (response.data.success) {
                setOtpVerified(true);
                setOtpError('');
                toast.success('OTP verified successfully!');
                setTimerActive(false); // Stop the timer
            } else {
                setOtpError(response.data.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setOtpError('An error occurred while verifying OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Set timeout for OTP expiration
    useEffect(() => {
        if (timerActive && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(interval);
        } else if (timer === 0) {
            setOtpSent(false); // Reset OTP sent state
            setOtpError('OTP expired. Please request a new OTP.');
            setOtpVerified(false); // Reset verified status
            setTimerActive(false); // Stop the timer
            setOtpResendEnabled(true); // Enable resend button
            setMobileDisabled(false); 
        }
    }, [timerActive, timer]);

    // Handle form submission
    const handleSubmit = async (values, { resetForm }) => {
        if (!otpVerified) {
            toast.error('Please verify the OTP before submitting the form.');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Enquiry/InsertBusinessEnquiry`, {
                name: values.name,
                mobileNo: values.mobileNo,
                email: values.email,
                city: values.city,
                pincode: values.pincode,
                company: values.company,
                businessType: values.businessType,
                estimatedPickupDaily: values.estimatedPickupDaily,
                contentOfTheShipment: values.contentOfTheShipment,
                wantedServiceType: values.wantedServiceType,
            });
            if (response.data.success) {
                setThankYouMessage(true); // Show thank-you message
                setOtpSent(false);
                setOtpVerified(false);
                setOtpInput('');
                resetForm();

                setTimeout(() => {
                    setThankYouMessage(false); // Hide the thank-you message
                    window.location.reload(); // Refresh the page
                }, 6000); // Refresh after 6 seconds
            } else {
                toast.error('Failed to submit enquiry. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting enquiry:', error);
            toast.error('An error occurred while submitting enquiry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Formik
                initialValues={{
                    name: '',
                    mobileNo: '',
                    email: '',
                    city: '',
                    pincode: '',
                    company: '',
                    businessType: '',
                    estimatedPickupDaily: '',
                    contentOfTheShipment: '',
                    wantedServiceType: '',
                    callMailAccepted: false,
                }}
                validationSchema={validationSchema}
                validateOnBlur={true} // Enable validation on blur
                validateOnChange={true} // Enable validation on change
                onSubmit={handleSubmit}
            >
                {({ values }) => (
                    <Form>
                        <div className="uk-child-width-1-2@m uk-grid-small uk-grid-match" data-uk-grid>
                            <div className='uk-width-1-1'>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Name:</label>
                                    <Field type="text" name="name" className='uk-input' />
                                    <ErrorMessage style={errorMessageStyle} name="name" component="div" className="error-message" />
                                </div>
                            </div>
                            <div>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Mobile No:</label>
                                    <Field type="text" name="mobileNo" className='uk-input' disabled={mobileDisabled} />
                                    <ErrorMessage style={errorMessageStyle} name="mobileNo" component="div" className="error-message" />
                                    {!otpVerified && (
                                        <button
                                            type="button" className='uk-margin-small-top'
                                            onClick={() => handleSendOtp(values.mobileNo)}
                                            disabled={!values.mobileNo || otpSent || loading} // Disable if mobile number is empty or loading
                                        >
                                            {loading ? 'Sending...' : 'Send OTP'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="uk-text-left">
                                    {otpSent && (
                                        <div>
                                            <label className="uk-form-label uk-text-bold">Enter OTP:</label>
                                            <Field type="text" 
                                                name="otp" 
                                                className='uk-input'
                                                value={otpInput}
                                                onChange={(e) => setOtpInput(e.target.value)}
                                                required
                                                disabled={otpVerified}
                                            />
                                            {!otpVerified && (
                                                <button
                                                    type="button"
                                                    className='uk-margin-small'
                                                    onClick={() => handleOtpVerify(values.mobileNo)}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Verifying...' : 'Verify OTP'}
                                                </button>
                                            )}
                                            {otpError && <span style={{ color: 'red' }}>{otpError}</span>}
                                            <div>
                                                {otpVerified ? (
                                                    <span style={{ color: 'green' }}>OTP is verified</span>
                                                ) : (
                                                    <span style={{ color: 'red' }}>{timer > 0 ? `${timer} seconds remaining` : 'OTP expired'}</span>
                                                )}
                                            </div>
                                            {otpResendEnabled && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSendOtp(values.mobileNo)}
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Sending...' : 'Resend OTP'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Email:</label>
                                    <Field type="email" name="email" className='uk-input' />
                                    <ErrorMessage style={errorMessageStyle} name="email" component="div" />
                                </div>
                            </div>
                            <div>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">City:</label>
                                    <Field type="text" name="city" className='uk-input' />
                                    <ErrorMessage style={errorMessageStyle} name="city" component="div" />
                                </div>
                            </div>
                            <div>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Pincode:</label>
                                    <Field type="text" name="pincode" className='uk-input' />
                                    <ErrorMessage style={errorMessageStyle} name="pincode" component="div" />
                                </div>
                            </div>
                            <div>
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Company:</label>
                                    <Field type="text" name="company" className='uk-input' />
                                    <ErrorMessage style={errorMessageStyle} name="company" component="div" />
                                </div>
                            </div>
                            <div className="">
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Business Type:</label>
                                    <Field as="select" name="businessType" className="uk-select">
                                        <option value="">Select Business Type...</option>
                                        <option value="Limited">Limited</option>
                                        <option value="Pvt Ltd">Pvt Ltd</option>
                                        <option value="Sole Proprietorship">Sole Proprietorship</option>
                                        <option value="LLP">LLP</option>
                                        <option value="General or limited partnership">General or limited partnership</option>
                                        <option value="Corporation">Corporation</option>
                                        <option value="Limited liability company (LLC)">Limited liability company (LLC)</option>
                                    </Field>
                                    <ErrorMessage style={errorMessageStyle} name="businessType" component="div" className="error-message" />
                                </div>
                            </div>

                            {/* Estimated Pickup Daily Dropdown */}
                            <div className="">
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Estimated Pickup Daily:</label>
                                    <Field as="select" name="estimatedPickupDaily" className="uk-select">
                                        <option value="">Pick...</option>
                                        <option value="0 to 5">0 to 5</option>
                                        <option value="5 to 10">5 to 10</option>
                                        <option value="10 to 15">10 to 15</option>
                                        <option value="15 to 20">15 to 20</option>
                                        <option value="20 to 25">20 to 25</option>
                                        <option value="25+">25+</option>
                                        <option value="50+">50+</option>
                                        <option value="100+">100+</option>
                                    </Field>
                                    <ErrorMessage style={errorMessageStyle} name="estimatedPickupDaily" component="div" className="error-message" />
                                </div>
                            </div>

                            {/* Content of the Shipment Dropdown */}
                            <div className="">
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Content of the Shipment:</label>
                                    <Field as="select" name="contentOfTheShipment" className="uk-select">
                                        <option value="">Choose...</option>
                                        <option value="Finished Garments">Finished Garments</option>
                                        <option value="Textiles">Textiles</option>
                                        <option value="Other industrial orient to textiles">Other industrial orient to textiles</option>
                                        <option value="Machinery Industry">Machinery Industry</option>
                                        <option value="Others">Others</option>
                                    </Field>
                                    <ErrorMessage style={errorMessageStyle} name="contentOfTheShipment" component="div" className="error-message" />
                                </div>
                            </div>

                            {/* Wanted Service Type Dropdown */}
                            <div className="">
                                <div className="uk-text-left">
                                    <label className="uk-form-label uk-text-bold">Wanted Service Type:</label>
                                    <Field as="select" name="wantedServiceType" className="uk-select">
                                        <option value="">Select...</option>
                                        <option value="Courier">Courier</option>
                                        <option value="Air Cargo">Air Cargo</option>
                                        <option value="Airport To Airport">Airport To Airport</option>
                                        <option value="Charter Flight">Charter Flight</option>
                                    </Field>
                                    <ErrorMessage style={errorMessageStyle} name="wantedServiceType" component="div" className="error-message" />
                                </div>
                            </div>
                            <div className='uk-width-1-1'>
                                <div className="uk-text-left">
                                    <Field type="checkbox" name="callMailAccepted" className="uk-checkbox" />
                                    <label className="uk-form-label uk-text-bold uk-margin-small-left">
                                    I agree and allow STRATOS AIRMAIL representative to call or e-mail regarding the enquiry.
                                    </label>
                                    <ErrorMessage style={errorMessageStyle} name="callMailAccepted" component="div" className="error-message" />
                                </div>
                            </div>
                            <div>
                                <button type="submit" className="uk-button uk-button-primary" disabled={!otpVerified || loading}>
                                    {loading ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                            <ToastContainer 
                                position="top-left" // Set the position (e.g., top-right, bottom-left)
                                autoClose={5000} // Auto close after 5 seconds
                                hideProgressBar={false} // Show progress bar
                                newestOnTop={false} // Newest toasts appear on top
                                closeOnClick // Close toast on click
                                rtl={false} // Right to left layout
                                pauseOnFocusLoss // Pause when the window is focused
                                draggable // Allow dragging
                                pauseOnHover // Pause when hovered
                                style={{ width: '300px' }}
                            />
                        </div>
                        {thankYouMessage && <div className="uk-alert-success" uk-alert="true">Thank you for your enquiry!</div>}
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default BusinessEnquiryForm;
 